import { useStowageStore } from 'src/context/StowageContext';
import { TITLE_MAX_LENGTH, createEmpyStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { useEffect, useState } from 'react';
import OdfjellInputComponent from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { getMasterStowagePlan } from 'src/utils/stowage/stowagePlanUtils';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { StowagePlanShareTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanShareTypeEnum';
import { InputContainer } from './PageHeaderStowagePublishPromotePanel.style';
import { ButtonActionType } from '../pageHeaderStowagePublishPromoteButton/PageHeaderStowagePublishPromoteButton';
import { useAuthStore } from 'src/context/AuthContext';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';

type Props = {
    type: ButtonActionType;
};

const PageHeaderStowagePublishPromotePanel: React.FC<Props> = (props: Props) => {
    const { activeStowagePlan, stowagePlans, setIsPublishPromoteOpen } = useStowageStore();
    const { updateStowagePlan, addStowagePlan } = usePostStowageApi();
    const { loggedInUser } = useAuthStore();
    const { setQueryParamStowagePlan } = useStowageQueryParamsUtils();
    const [titleInput, setTitleInput] = useState<string>('');
    const [commentsInput, setCommentsInput] = useState<string>('');

    useEffect(() => {
        if (!activeStowagePlan) {
            return;
        }

        let newTitle = activeStowagePlan.title;
        if (newTitle.length > TITLE_MAX_LENGTH) {
            newTitle = newTitle.slice(0, TITLE_MAX_LENGTH);
        }

        setTitleInput(newTitle);
        setCommentsInput(activeStowagePlan.comments || '');
    }, [activeStowagePlan]);

    const onTitleChange = (newTitle: string) => {
        setTitleInput(newTitle);
    };

    const onCommentsChange = (newComments: string) => {
        setCommentsInput(newComments);
    };

    const onPublishStowagePlan = async () => {
        const _stowagePlan = {
            ...activeStowagePlan,
            type: StowagePlanTypeEnum.Published,
            shareType: StowagePlanShareTypeEnum.Shared,
            title: titleInput,
            comments: commentsInput,
        };
        updateStowagePlan(_stowagePlan);
    };

    const onPromoteStowagePlan = async () => {
        if (!activeStowagePlan) return;

        const previousMasterPlan = getMasterStowagePlan(stowagePlans);
        const newMasterPlanVersion = (previousMasterPlan?.masterPlanVersion || 0) + 1;

        let newPlan = createEmpyStowagePlan(
            activeStowagePlan.imoNumber,
            loggedInUser?.fullName || loggedInUser?.userName || activeStowagePlan.owner,
            activeStowagePlan.stowagePlanGuid,
            titleInput,
            newMasterPlanVersion
        );

        newPlan = {
            ...newPlan,
            type: StowagePlanTypeEnum.Master,
            shareType: StowagePlanShareTypeEnum.Shared,
            comments: commentsInput,
        };

        const { updatedStowagePlan } = await addStowagePlan(newPlan);
        if (updatedStowagePlan) {
            setQueryParamStowagePlan(updatedStowagePlan.stowagePlanGuid, ['portCallGuid', 'berthCallGuid', 'cargoOperationGuid']);
        }
    };

    const onPublishPromoteStowagePlan = () => {
        if (props.type === 'publish') {
            onPublishStowagePlan();
        } else if (props.type === 'promote') {
            onPromoteStowagePlan();
        }
        onClose();
    };

    const onClose = () => {
        setIsPublishPromoteOpen(false);
    };

    return (
        <OdfjellPanel
            headerTitle={(props.type === 'promote' ? 'Promote' : 'Publish') + ' stowage plan'}
            defaultPosition="center"
            panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
            hasCurtain={true}
            preventOutsideClose={true}
            onCancel={onClose}
            onAction={onPublishPromoteStowagePlan}
            showCancelButton={true}
        >
            <InputContainer>
                <OdfjellInputComponent
                    description="Title"
                    type="text"
                    isReadOnly={false}
                    value={titleInput}
                    onChange={(e) => onTitleChange(e.event.target.value)}
                    maxTextLength={50}
                />
            </InputContainer>
            <InputContainer>
                <OdfjellInputComponent
                    description="Comments"
                    type="textarea"
                    isReadOnly={false}
                    value={commentsInput}
                    onChange={(e) => onCommentsChange(e.event.target.value)}
                    maxTextLength={500}
                />
            </InputContainer>
        </OdfjellPanel>
    );
};

export default PageHeaderStowagePublishPromotePanel;
